document.addEventListener('DOMContentLoaded', () => {
  const blocks = Array.from(document.querySelectorAll('.js-shortcode-products'));
  if (!blocks.length) return;

  // ページ全体で使われているハンドルをユニークに収集
  const allHandles = new Set();

  blocks.forEach(block => {
    const handlesStr = block.getAttribute('data-product-handles') || '';
    handlesStr.split(',').forEach(h => {
      const handle = h.trim();
      if (handle) allHandles.add(handle);
    });
  });

  if (!allHandles.size) return;

  // まとめて Storefront API で取得（バッチ分割にも対応）
  fetchProductsInBatches(Array.from(allHandles))
    .then(productsMap => {
      blocks.forEach(block => renderShortcodeBlock(block, productsMap));
    })
    .catch(err => {
      console.error('Shortcode products fetch error:', err);
    });
});

// ===== Storefront API 呼び出し周り =====

const STOREFRONT_ENDPOINT = '/api/2024-10/graphql.json'; // バージョンは必要に応じて変更
const STOREFRONT_TOKEN = 'e016f3fe27d392a3a2ab20f90dac547d';  // カスタムアプリで発行済みのトークン

async function fetchProductsInBatches(handles, batchSize = 30) {
  const map = new Map();
  const batches = [];

  for (let i = 0; i < handles.length; i += batchSize) {
    batches.push(handles.slice(i, i + batchSize));
  }

  for (const batch of batches) {
    const batchMap = await fetchProductsByHandles(batch);
    batchMap.forEach((product, handle) => {
      map.set(handle, product);
    });
  }

  return map;
}

async function fetchProductsByHandles(handles) {
  const fields = `
    id
    handle
    title
    onlineStoreUrl
    availableForSale
    images(first: 1) {
      edges {
        node {
          url
          altText
        }
      }
    }
    priceRange {
      minVariantPrice {
        amount
        currencyCode
      }
    }
    variants(first: 50) {
      edges {
        node {
          id
          availableForSale
          price {
            amount
            currencyCode
          }
        }
      }
    }
  `;

  let queryBody = 'query ProductsByHandle {';

  handles.forEach((handle, idx) => {
    const alias = `p${idx}`; // GraphQL用の識別子
    queryBody += `
      ${alias}: product(handle: "${handle.replace(/"/g, '\\"')}") {
        ${fields}
      }
    `;
  });

  queryBody += '}';

  const res = await fetch(STOREFRONT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
    },
    body: JSON.stringify({ query: queryBody })
  });

  if (!res.ok) {
    throw new Error(`Storefront API error: ${res.status}`);
  }

  const json = await res.json();

  const map = new Map();
  handles.forEach((handle, idx) => {
    const alias = `p${idx}`;
    const node = json?.data?.[alias];
    if (node) {
      map.set(handle, node);
    }
  });

  return map;
}

// ===== ブロックごとの描画 =====

function renderShortcodeBlock(block, productsMap) {
  const handlesStr = block.getAttribute('data-product-handles') || '';
  const idsStr = block.getAttribute('data-product-ids') || '';

  const handles = handlesStr.split(',').map(s => s.trim());
  const ids = idsStr.split(',').map(s => s.trim());

  let html = '<div class="ile-shortcode-products">';

  handles.forEach((handle, index) => {
    if (!handle) return;

    const product = productsMap.get(handle);
    if (!product) return;

    const image = product.images?.edges?.[0]?.node;
    const url = product.onlineStoreUrl || `/products/${handle}`;

    const variants = product.variants?.edges || [];
    const hasMultipleVariants = variants.length > 1;

    const specifiedVariantIdStr = (ids[index] || '').trim(); // 数値の variant.id を想定
    const hasSpecifiedVariantId = specifiedVariantIdStr.length > 0;

    let amount = null;
    let currencyCode = 'JPY';
    let priceText = '';

    if (hasSpecifiedVariantId) {
      // ▼ product_ids が指定されている場合：そのバリアントの価格＋売り切れ判定
      const variantNode = findVariantByLegacyId(variants, specifiedVariantIdStr);

      if (!variantNode || !variantNode.availableForSale) {
        // 指定バリアントが存在しない or 売り切れ
        priceText = '売り切れ';
      } else {
        amount = variantNode.price?.amount;
        currencyCode = variantNode.price?.currencyCode || 'JPY';
        if (amount != null) {
          priceText = formatPrice(amount, currencyCode);
        }
      }
    } else {
      // ▼ product_ids 未指定の場合：商品全体の availableForSale で判定
      if (!product.availableForSale) {
        priceText = '売り切れ';
      } else {
        const priceInfo = product.priceRange?.minVariantPrice;
        amount = priceInfo?.amount;
        currencyCode = priceInfo?.currencyCode || 'JPY';

        if (amount != null) {
          priceText = formatPrice(amount, currencyCode);
          // バリエーションが複数ある場合のみ「〜」を付加
          if (hasMultipleVariants) {
            priceText += '〜';
          }
        }
      }
    }

    html += `
      <div class="ile-shortcode-product-card">
        <a href="${url}">
          <div class="ile-shortcode-product-image">
            ${
              image
                ? `<img src="${image.url}" alt="${escapeHtml(image.altText || product.title)}">`
                : ''
            }
          </div>
          <div class="ile-shortcode-product-info">
            <div class="ile-shortcode-product-title card__name h4">${escapeHtml(product.title)}</div>
            ${
              priceText
                ? `<div class="ile-shortcode-product-price card__price">${priceText}</div>`
                : ''
            }
            <span class="ile-shortcode-product-btn">商品ページを見る</span>
          </div>
        </a>
      </div>
    `;
  });

  html += '</div>';
  block.innerHTML = html;
}

// ===== ユーティリティ =====
function findVariantByLegacyId(variantEdges, legacyIdStr) {
  const target = String(legacyIdStr).trim();
  if (!target) return null;

  for (const edge of variantEdges) {
    const v = edge.node;
    if (!v || !v.id) continue;

    let decoded;
    try {
      decoded = atob(v.id); // base64 → "gid://shopify/ProductVariant/123456789"
    } catch (e) {
      continue;
    }

    const parts = decoded.split('/');
    const last = parts[parts.length - 1] || '';

    if (last === target) {
      return v;
    }
  }

  return null;
}

function formatPrice(amount, currencyCode) {
  const num = Number(amount);
  if (Number.isNaN(num)) return '';

  try {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: currencyCode || 'JPY'
    }).format(num);
  } catch (e) {
    // Intl に対応していない古いブラウザ向けフォールバック
    return `${num.toLocaleString()} ${currencyCode || ''}`;
  }
}

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}